import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { HomeRedirect } from './components/auth/ScreenGate';
import { SecurityProvider } from './components/auth/SecurityProvider';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Layouts loaded eagerly (needed for shell)
import AdminLayout from './components/layouts/AdminLayout';
import { CompanyRoute, LegacyAccountRoute } from './components/crm/CrmLegacyRoutes';
import { SettingsLayout } from './components/layouts/SettingsLayout';

// Auth pages loaded eagerly (first screen for unauthenticated users)
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OAuthConsentPage from './pages/OAuthConsentPage';

// All admin pages lazy-loaded for code splitting (with auto-retry on chunk failure)
const HomePage = lazyWithRetry(() => import('./pages/admin/HomePage'));
const MarketingPersonasPage = lazyWithRetry(() => import('./pages/admin/MarketingPersonasPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/admin/DashboardPage'));
const AdminOverviewPage = lazyWithRetry(() => import('./pages/admin/AdminOverviewPage'));
const StatisticsPage = lazyWithRetry(() => import('./pages/admin/StatisticsPage'));
const FeaturesManagementPage = lazyWithRetry(() => import('./pages/admin/FeaturesManagementPage'));
const MobileVoiceNotePage = lazyWithRetry(() => import('./pages/admin/MobileVoiceNotePage'));
const MobileScanCardPage = lazyWithRetry(() => import('./pages/admin/MobileScanCardPage'));
const MobileNearbyPage = lazyWithRetry(() => import('./pages/admin/MobileNearbyPage'));
const CommentsManagementPage = lazyWithRetry(() => import('./pages/admin/CommentsManagementPage'));
const UsersManagementPage = lazyWithRetry(() => import('./pages/admin/UsersManagementPage'));
const FeatureEditPage = lazyWithRetry(() => import('./pages/admin/FeatureEditPage'));
const CommentModPage = lazyWithRetry(() => import('./pages/admin/CommentModPage'));
const NotificationTemplatesPage = lazyWithRetry(() => import('./pages/admin/NotificationTemplatesPage'));
const NotificationCenterPage = lazyWithRetry(() => import('./pages/admin/NotificationCenterPage'));
const NotificationPreferencesPage = lazyWithRetry(() => import('./pages/admin/NotificationPreferencesPage'));
const SettingsDevicesPage = lazyWithRetry(() => import('./pages/admin/SettingsDevicesPage'));
const StripeEventsPage = lazyWithRetry(() => import('./pages/admin/StripeEventsPage'));
const NewsletterBuilderPage = lazyWithRetry(() => import('./pages/admin/NewsletterBuilderPage'));
const NewsletterTemplatesPage = lazyWithRetry(() => import('./pages/admin/NewsletterTemplatesPage'));
const NewslettersListPage = lazyWithRetry(() => import('./pages/admin/NewslettersListPage'));
const KBArticlesPage = lazyWithRetry(() => import('./pages/admin/KBArticlesPage'));
const KBArticleEditPage = lazyWithRetry(() => import('./pages/admin/KBArticleEditPage'));
const KBCategoriesPage = lazyWithRetry(() => import('./pages/admin/KBCategoriesPage'));
const StorageDiagnosticPage = lazyWithRetry(() => import('./pages/admin/StorageDiagnosticPage'));
const StorageBucketSetupPage = lazyWithRetry(() => import('./pages/admin/StorageBucketSetupPage'));
const VersionsPage = lazyWithRetry(() => import('./pages/admin/VersionsPage'));
const VersionEditPage = lazyWithRetry(() => import('./pages/admin/VersionEditPage'));
const BrokenLinksPage = lazyWithRetry(() => import('./pages/admin/BrokenLinksPage'));
const IndexationPage = lazyWithRetry(() => import('./pages/admin/IndexationPage'));
const NewsletterAnalyticsPage = lazyWithRetry(() => import('./pages/admin/NewsletterAnalyticsPage'));
const NewsletterDashboardPage = lazyWithRetry(() => import('./pages/admin/NewsletterDashboardPage'));
const NewsletterSubscribersPage = lazyWithRetry(() => import('./pages/admin/NewsletterSubscribersPage'));
const CRMAccountsPage = lazyWithRetry(() => import('./pages/admin/CRMAccountsPage'));
const CRMCompanyDetailPage = lazyWithRetry(() => import('./pages/admin/CRMCompanyDetailPage'));
const CRMOpportunityDetailPage = lazyWithRetry(() => import('./pages/admin/CRMOpportunityDetailPage'));
const OpportunitiesPage = lazyWithRetry(() => import('./pages/admin/OpportunitiesPage'));
const CRMContactsPage = lazyWithRetry(() => import('./pages/admin/CRMContactsPage'));
const CRMGroupsDetectionPage = lazyWithRetry(() => import('./pages/admin/CRMGroupsDetectionPage'));
const CRMDuplicatesPage = lazyWithRetry(() => import('./pages/admin/CRMDuplicatesPage'));
const CRMContactDetailPage = lazyWithRetry(() => import('./pages/admin/CRMContactDetailPage'));
const CRMProductsPage = lazyWithRetry(() => import('./pages/admin/CRMProductsPage'));
const CRMCarteMapPage = lazyWithRetry(() => import('./pages/admin/CRMCarteMapPage'));
const GoogleContactsSyncPage = lazyWithRetry(() => import('./pages/admin/GoogleContactsSyncPage'));
const AppointmentsPage = lazyWithRetry(() => import('./pages/admin/AppointmentsPage'));
const AppointmentTypesPage = lazyWithRetry(() => import('./pages/admin/AppointmentTypesPage'));
const AvailabilityPage = lazyWithRetry(() => import('./pages/admin/AvailabilityPage'));
const AppointmentsSettingsPage = lazyWithRetry(() => import('./pages/admin/AppointmentsSettingsPage'));
const MeetingReportSettingsPage = lazyWithRetry(() => import('./pages/admin/MeetingReportSettingsPage'));
const SkillsSettingsPage = lazyWithRetry(() => import('./pages/admin/SkillsSettingsPage'));
const WritingProfileSettingsPage = lazyWithRetry(() => import('./pages/admin/WritingProfileSettingsPage'));
const ContactPortraitsSettingsPage = lazyWithRetry(() => import('./pages/admin/ContactPortraitsSettingsPage'));
const NotesPage = lazyWithRetry(() => import('./pages/admin/NotesPage'));
const TasksPage = lazyWithRetry(() => import('./pages/admin/TasksPage'));
const ApiDocumentationPage = lazyWithRetry(() => import('./pages/admin/ApiDocumentationPage'));
const ApiManagementPage = lazyWithRetry(() => import('./pages/admin/ApiManagementPage'));
const CompanySettingsPage = lazyWithRetry(() => import('./pages/admin/CompanySettingsPage'));
const OrganizationSettingsPage = lazyWithRetry(() => import('./pages/admin/OrganizationSettingsPage'));
const OnboardingTemplatePage = lazyWithRetry(() => import('./pages/admin/OnboardingTemplatePage'));
const OnboardingInstancesPage = lazyWithRetry(() => import('./pages/admin/OnboardingInstancesPage'));
const OnboardingLeadsPage = lazyWithRetry(() => import('./pages/admin/OnboardingLeadsPage'));
const ClientAccountsPage = lazyWithRetry(() => import('./pages/admin/ClientAccountsPage'));
const BacklogManagementPage = lazyWithRetry(() => import('./pages/admin/BacklogManagementPage'));
const MyDashboardPage = lazyWithRetry(() => import('./pages/admin/MyDashboardPage'));
const RoadmapPreviewPage = lazyWithRetry(() => import('./pages/admin/RoadmapPreviewPage'));
const FeatureAnalyticsPage = lazyWithRetry(() => import('./pages/admin/FeatureAnalyticsPage'));
const FeatureAnnouncementsPage = lazyWithRetry(() => import('./pages/admin/FeatureAnnouncementsPage'));
const LinkedInPlannerPage = lazyWithRetry(() => import('./pages/admin/LinkedInPlannerPage'));
const LinkedInPostEditPage = lazyWithRetry(() => import('./pages/admin/LinkedInPostEditPage'));
const MyProfilePage = lazyWithRetry(() => import('./pages/admin/MyProfilePage'));
const WidgetConfigPage = lazyWithRetry(() => import('./pages/admin/WidgetConfigPage'));
const RevenueDashboardPage = lazyWithRetry(() => import('./pages/admin/RevenueDashboardPage'));
const BillingPage = lazyWithRetry(() => import('./pages/admin/BillingPage'));
const BusinessCasePage = lazyWithRetry(() => import('./pages/admin/BusinessCasePage'));
const EventsPage = lazyWithRetry(() => import('./pages/admin/EventsPage'));
const EventCreatePage = lazyWithRetry(() => import('./pages/admin/EventCreatePage'));
const EventDetailPage = lazyWithRetry(() => import('./pages/admin/EventDetailPage'));
const RobotTermsPage = lazyWithRetry(() => import('./pages/admin/RobotTermsPage'));
const InboxPage = lazyWithRetry(() => import('./pages/admin/InboxPage'));
const InboxAnalyticsPage = lazyWithRetry(() => import('./pages/admin/InboxAnalyticsPage'));
const WidgetUsersPage = lazyWithRetry(() => import('./pages/admin/WidgetUsersPage'));
const AnonymousVisitorsPage = lazyWithRetry(() => import('./pages/admin/AnonymousVisitorsPage'));
const ClientFeatureRequestsPage = lazyWithRetry(() => import('./pages/admin/ClientFeatureRequestsPage'));
const ClientAnnouncementsPage = lazyWithRetry(() => import('./pages/admin/ClientAnnouncementsPage'));
const ProductIntelligencePage = lazyWithRetry(() => import('./pages/admin/ProductIntelligencePage'));
const ClientSurveysPage = lazyWithRetry(() => import('./pages/admin/ClientSurveysPage'));
const WidgetToursPage = lazyWithRetry(() => import('./pages/admin/WidgetToursPage'));
const WidgetBadgesPage = lazyWithRetry(() => import('./pages/admin/WidgetBadgesPage'));
const AiCockpitPage = lazyWithRetry(() => import('./pages/admin/AiCockpitPage'));
const RelationsHubPage = lazyWithRetry(() => import('./pages/admin/RelationsHubPage'));
const WorkQueuePage = lazyWithRetry(() => import('./pages/admin/WorkQueuePage'));
const AutonomyPage = lazyWithRetry(() => import('./pages/admin/AutonomyPage'));
const EquipePage = lazyWithRetry(() => import('./pages/admin/EquipePage'));
const QualityPage = lazyWithRetry(() => import('./pages/admin/QualityPage'));

export default function App() {
  const { loading } = useAuth();

  // Only show loading spinner for max 3 seconds to prevent infinite loading
  const [showLoading, setShowLoading] = React.useState(true);

  React.useEffect(() => {
    // If loading completes, immediately update showLoading
    if (!loading) {
      setShowLoading(false);
    }

    // Set a maximum timeout for showing the loading spinner
    const timeout = setTimeout(() => {
      setShowLoading(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [loading]);

  if (showLoading && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0066FF]"></div>
      </div>
    );
  }

  return (
    <SecurityProvider>
      <Router>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* OAuth 2.1 consent page (Supabase OAuth Server — self-manages auth redirect) */}
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />

          {/* Redirect root to admin dashboard */}
          <Route path="/" element={<Navigate to="/admin" replace />} />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            {/* Le Bureau est la page d'accueil (PLAN-BUREAU) d'un administrateur ; un commercial arrive sur le CRM (lot L6g). */}
            <Route index element={<HomeRedirect />} />
            <Route path="tableau-de-bord" element={<HomePage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="overview" element={<AdminOverviewPage />} />
            <Route path="statistics" element={<StatisticsPage />} />
            {/* Retired (PLAN-GENERICITE phase 1): old links lead to Travail, those of the TICKET_* notifications included. */}
            <Route path="moderation" element={<Navigate to="/admin/travail" replace />} />
            <Route path="tickets" element={<Navigate to="/admin/travail" replace />} />

            {/* Revenue Dashboard */}
            <Route path="revenue" element={<RevenueDashboardPage />} />
            <Route path="facturation" element={<BillingPage />} />
            <Route path="business-case" element={<BusinessCasePage />} />
            <Route path="evenements" element={<EventsPage />} />
            <Route path="evenements/nouveau" element={<EventCreatePage />} />
            <Route path="evenements/:id" element={<EventDetailPage />} />
            <Route path="robot/conditions" element={<RobotTermsPage />} />

            {/* Inbox — messagerie partagée (Client Platform) */}
            <Route path="inbox" element={<InboxPage />} />
            <Route path="inbox-analytics" element={<InboxAnalyticsPage />} />
            <Route path="product-intelligence" element={<ProductIntelligencePage />} />
            {/* Statique avant le segment dynamique :conversationId (priorité de route). */}
            <Route path="inbox/users" element={<WidgetUsersPage />} />
            <Route path="inbox/visitors" element={<AnonymousVisitorsPage />} />
            <Route path="inbox/:conversationId" element={<InboxPage />} />

            {/* Relations Hub — boîte intelligente 3 volets en lecture (PLAN-RELATIONS-HUB V1) */}
            <Route path="relations" element={<RelationsHubPage />} />
            <Route path="relations/:threadKey" element={<RelationsHubPage />} />

            {/* File de travail — file unifiée en lecture (PLAN-WORK-QUEUE Phase 2) */}
            <Route path="travail" element={<WorkQueuePage />} />
            <Route path="travail/:source/:sourceId" element={<WorkQueuePage />} />
            <Route path="autonomie" element={<AutonomyPage />} />
            <Route path="equipe" element={<EquipePage />} />
            <Route path="equipe/:slug" element={<EquipePage />} />

            {/* CRM routes */}
            <Route path="crm" element={<Navigate to="/admin/crm/opportunities" replace />} />
            <Route path="crm/opportunities" element={<OpportunitiesPage />} />
            <Route path="crm/accounts" element={<CRMAccountsPage />} />
            <Route path="crm/carte" element={<CRMCarteMapPage />} />
            <Route path="crm/company/:id" element={<CompanyRoute><CRMCompanyDetailPage /></CompanyRoute>} />
            <Route path="crm/contacts" element={<CRMContactsPage />} />
            <Route path="crm/contacts/:id" element={<CRMContactDetailPage />} />
            <Route path="crm/opportunities/:id" element={<CRMOpportunityDetailPage />} />
            <Route path="crm/leads/:id" element={<CRMCompanyDetailPage />} />
            <Route path="crm/accounts/:id" element={<LegacyAccountRoute />} />
            <Route path="crm/duplicates" element={<CRMDuplicatesPage />} />
            <Route path="crm/groups-detection" element={<CRMGroupsDetectionPage />} />
            <Route path="crm/google-sync" element={<GoogleContactsSyncPage />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="actions" element={<Navigate to="/admin/travail" replace />} />

            {/* Personal routes (user dropdown) */}
            <Route path="notes" element={<NotesPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="profile" element={<MyProfilePage />} />

            {/* Onboarding routes */}
            <Route path="onboarding" element={<Navigate to="/admin/onboarding/instances" replace />} />
            <Route path="onboarding/instances" element={<OnboardingInstancesPage />} />
            <Route path="onboarding/leads" element={<OnboardingLeadsPage />} />
            <Route path="onboarding/template" element={<OnboardingTemplatePage />} />
            <Route path="client-accounts" element={<ClientAccountsPage />} />

            {/* Contenu routes */}
            <Route path="kb/articles" element={<KBArticlesPage />} />
            <Route path="kb/articles/:id" element={<KBArticleEditPage />} />
            <Route path="newsletter" element={<NewslettersListPage />} />
            <Route path="newsletter/create" element={<NewsletterBuilderPage />} />
            <Route path="newsletter/edit/:id" element={<NewsletterBuilderPage />} />
            <Route path="newsletter/dashboard" element={<NewsletterDashboardPage />} />
            <Route path="newsletter/templates" element={<NewsletterTemplatesPage />} />
            <Route path="newsletter/subscribers" element={<NewsletterSubscribersPage />} />
            <Route path="newsletter/analytics" element={<NewsletterAnalyticsPage />} />
            <Route path="newsletter/analytics/:id" element={<NewsletterAnalyticsPage />} />
            <Route path="personas" element={<MarketingPersonasPage />} />
            <Route path="linkedin" element={<LinkedInPlannerPage />} />
            <Route path="linkedin/:id" element={<LinkedInPostEditPage />} />
            <Route path="comments" element={<CommentsManagementPage />} />
            <Route path="comments/:id" element={<CommentModPage />} />

            {/* Produit routes */}
            <Route path="my-dashboard" element={<MyDashboardPage />} />
            <Route path="backlog" element={<BacklogManagementPage />} />
            <Route path="versions" element={<VersionsPage />} />
            <Route path="versions/:id" element={<VersionEditPage />} />
            <Route path="features" element={<FeaturesManagementPage />} />
            <Route path="feature-requests" element={<ClientFeatureRequestsPage />} />
            <Route path="client-announcements" element={<ClientAnnouncementsPage />} />
            <Route path="client-surveys" element={<ClientSurveysPage />} />
            <Route path="widget-tours" element={<WidgetToursPage />} />
            <Route path="widget-badges" element={<WidgetBadgesPage />} />
            <Route path="mobile/voice-note" element={<MobileVoiceNotePage />} />
            <Route path="mobile/scan-card" element={<MobileScanCardPage />} />
            <Route path="mobile/nearby" element={<MobileNearbyPage />} />
            <Route path="otto-todo" element={<Navigate to="/admin/travail" replace />} />
            <Route path="otto-backlog" element={<Navigate to="/admin/travail" replace />} />
            <Route path="cockpit-ia" element={<AiCockpitPage />} />
            <Route path="qualite" element={<QualityPage />} />
            <Route path="features/:id" element={<FeatureEditPage />} />
            <Route path="roadmap-preview" element={<RoadmapPreviewPage />} />
            <Route path="feature-analytics" element={<FeatureAnalyticsPage />} />
            <Route path="feature-announcements" element={<FeatureAnnouncementsPage />} />

            {/* Settings routes (nested layout) */}
            <Route path="settings" element={<SettingsLayout />}>
              <Route index element={null} />
              <Route path="organization" element={<OrganizationSettingsPage />} />
              <Route path="company" element={<CompanySettingsPage />} />
              <Route path="users" element={<UsersManagementPage />} />
              <Route path="api" element={<ApiManagementPage />} />
              <Route path="appointment-types" element={<AppointmentTypesPage />} />
              <Route path="appointment-availability" element={<AvailabilityPage />} />
              <Route path="appointment-settings" element={<AppointmentsSettingsPage />} />
              <Route path="meeting-reports" element={<MeetingReportSettingsPage />} />
              <Route path="kb-categories" element={<KBCategoriesPage />} />
              <Route path="notification-templates" element={<NotificationTemplatesPage />} />
              <Route path="notification-center" element={<NotificationCenterPage />} />
              <Route path="notification-preferences" element={<NotificationPreferencesPage />} />
              <Route path="devices" element={<SettingsDevicesPage />} />
              <Route path="onboarding-configs" element={<Navigate to="/admin/onboarding/template" replace />} />
              <Route path="stripe-products" element={<CRMProductsPage />} />
              <Route path="stripe-events" element={<StripeEventsPage />} />
              <Route path="indexation" element={<IndexationPage />} />
              <Route path="broken-links" element={<BrokenLinksPage />} />
              <Route path="widget-config" element={<WidgetConfigPage />} />
              <Route path="skills" element={<SkillsSettingsPage />} />
              <Route path="writing-profile" element={<WritingProfileSettingsPage />} />
              <Route path="contact-portraits" element={<ContactPortraitsSettingsPage />} />
            </Route>

            {/* Misc / utility routes */}
            <Route path="api-docs" element={<ApiDocumentationPage />} />
            <Route path="storage-diagnostic" element={<StorageDiagnosticPage />} />
            <Route path="storage-setup" element={<StorageBucketSetupPage />} />

            {/* Backward compatibility redirects (old paths → settings) */}
            <Route path="users" element={<Navigate to="/admin/settings/users" replace />} />
            <Route path="appointments/types" element={<Navigate to="/admin/settings/appointment-types" replace />} />
            <Route path="appointments/availability" element={<Navigate to="/admin/settings/appointment-availability" replace />} />
            <Route path="appointments/settings" element={<Navigate to="/admin/settings/appointment-settings" replace />} />
            <Route path="kb/categories" element={<Navigate to="/admin/settings/kb-categories" replace />} />
            <Route path="notifications" element={<Navigate to="/admin/settings/notification-templates" replace />} />
            <Route path="notification-center" element={<Navigate to="/admin/settings/notification-center" replace />} />
            <Route path="onboarding/configs" element={<Navigate to="/admin/onboarding/template" replace />} />
            <Route path="crm/products" element={<Navigate to="/admin/settings/stripe-products" replace />} />
            <Route path="stripe-events" element={<Navigate to="/admin/settings/stripe-events" replace />} />
            <Route path="indexation" element={<Navigate to="/admin/settings/indexation" replace />} />
            <Route path="broken-links" element={<Navigate to="/admin/settings/broken-links" replace />} />
            <Route path="api-management" element={<Navigate to="/admin/settings/api" replace />} />
          </Route>

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Router>
    </SecurityProvider>
  );
}

export { App };
